import { createStore, createEffect, combine, sample } from 'effector';
import { createGate } from 'effector-react';
import { signout } from '@features/common';
import { getComplexes, getBuildings, getApartments } from './api';
import type {
  Complex,
  Building,
  Apartment,
  ObjectType,
  GetComplexesResponse,
  GetBuildingsResponse,
  GetApartmentsResponse,
  ComplexTree,
  BuildingTree,
} from './types';

export const complexKey = 'complex';
export const buildingKey = 'building';
const apartmentKey = 'apartment';

export const defaultObjectTypes: ObjectType[] = [complexKey, buildingKey, apartmentKey];

export const ObjectsTreeControlGate = createGate<{
  objectTypes: ObjectType[];
}>({
  defaultState: {
    objectTypes: defaultObjectTypes,
  },
});

export const $complexes = createStore<Complex[]>([]);
export const $buildings = createStore<Building[]>([]);
export const $apartments = createStore<Apartment[]>([]);

export const $isComplexesLoading = createStore(false);
export const $isBuildingsLoading = createStore(false);
export const $isApartmentsLoading = createStore(false);

export const $isLoading = combine(
  $isComplexesLoading,
  $isBuildingsLoading,
  $isApartmentsLoading,
  (isComplexesLoading, isBuildingsLoading, isApartmentsLoading) =>
    isComplexesLoading || isBuildingsLoading || isApartmentsLoading
);

export const $objects = combine(
  $complexes,
  $buildings,
  $apartments,
  ObjectsTreeControlGate.state,
  (
    complexes,
    buildings,
    apartments,
    { objectTypes }
  ): ComplexTree[] | BuildingTree[] | Apartment[] => {
    const hasComplexes = objectTypes.includes(complexKey);
    const hasBuildings = objectTypes.includes(buildingKey);
    const hasApartments = objectTypes.includes(apartmentKey);

    const isComplexLeaf = hasComplexes && !hasBuildings;
    const isBuildingLeaf = hasBuildings && !hasApartments;

    const getNodeId = (
      type: typeof complexKey | typeof buildingKey,
      id: number | string,
      isLeaf: boolean
    ) => (isLeaf ? id : `${type}-${id}`);

    const apartmentsByBuildingId = apartments.reduce<Record<string, Apartment[]>>(
      (acc, apartment) => {
        const parentBuildingId = String(apartment.parentId);

        if (!acc[parentBuildingId]) {
          acc[parentBuildingId] = [];
        }

        acc[parentBuildingId].push(apartment);

        return acc;
      },
      {}
    );

    const buildingsByComplexId = buildings.reduce<Record<string, BuildingTree[]>>(
      (acc, building) => {
        const parentComplexId = String(building.parentId);
        const children = hasApartments
          ? (apartmentsByBuildingId[String(building.id)] ?? [])
          : undefined;

        const hasChildren = Boolean(children?.length);

        if (!isBuildingLeaf && !hasChildren) {
          return acc;
        }

        if (!acc[parentComplexId]) {
          acc[parentComplexId] = [];
        }

        acc[parentComplexId].push({
          ...building,
          id: getNodeId(buildingKey, building.id, isBuildingLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      },
      {}
    );

    if (hasComplexes) {
      return complexes.reduce<ComplexTree[]>((acc, complex) => {
        const children = hasBuildings
          ? (buildingsByComplexId[String(complex.id)] ?? [])
          : undefined;

        const hasChildren = Boolean(children?.length);

        if (!isComplexLeaf && !hasChildren) {
          return acc;
        }

        acc.push({
          ...complex,
          id: getNodeId(complexKey, complex.id, isComplexLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      }, []);
    }

    if (hasBuildings) {
      return buildings.reduce<BuildingTree[]>((acc, building) => {
        const children = hasApartments
          ? (apartmentsByBuildingId[String(building.id)] ?? [])
          : undefined;

        const hasChildren = Boolean(children?.length);

        if (!isBuildingLeaf && !hasChildren) {
          return acc;
        }

        acc.push({
          ...building,
          id: getNodeId(buildingKey, building.id, isBuildingLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      }, []);
    }

    return apartments;
  }
);

export const fxGetComplexes = createEffect<void, GetComplexesResponse, Error>().use(
  getComplexes
);

export const fxGetBuildings = createEffect<void, GetBuildingsResponse, Error>().use(
  getBuildings
);

export const fxGetApartments = createEffect<void, GetApartmentsResponse, Error>().use(
  getApartments
);

/** Init */
/** Запрос ЖК */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(complexKey),
  target: fxGetComplexes,
});

/** Запрос зданий */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(buildingKey),
  target: fxGetBuildings,
});

/** Запрос квартир */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(apartmentKey),
  target: fxGetApartments,
});

$complexes
  .on(fxGetComplexes.doneData, (_, result) =>
    Array.isArray(result?.items)
      ? result.items.map((item) => ({
          id: item?.id,
          label: item?.title,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isComplexesLoading
  .on(fxGetComplexes.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);

$buildings
  .on(fxGetBuildings.doneData, (_, result) =>
    Array.isArray(result?.buildings)
      ? result.buildings.map((item) => ({
          id: item?.id,
          label: item?.title,
          parentId: item?.complex?.id,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isBuildingsLoading
  .on(fxGetBuildings.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);

$apartments
  .on(fxGetApartments.doneData, (_, result) =>
    Array.isArray(result?.items)
      ? result.items.map((item) => ({
          id: item?.apartment?.id,
          label: item?.apartment?.title,
          parentId: item?.building?.id,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isApartmentsLoading
  .on(fxGetApartments.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);
